import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Phone, Mail, MapPin } from 'lucide-react';

export const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-cream-dark to-cream border-t border-amber-100" data-testid="footer">
      <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center shadow-md">
                <span className="text-white font-serif font-bold text-lg">A</span>
              </div>
              <div>
                <h3 className="font-serif text-lg font-bold text-foreground">
                  Astro Vastu Remedies
                </h3>
              </div>
            </Link>
            <p className="text-muted-foreground text-sm mb-6">
              Your one-stop destination for authentic gemstones, healing crystals, and spiritual accessories. 
              All products are energized by certified astrologers.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/astro_vasturemedies"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-foreground hover:text-gold hover:shadow-md transition-all"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/AstroVastuRemedies"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-foreground hover:text-gold hover:shadow-md transition-all"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-foreground hover:text-gold hover:shadow-md transition-all"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Home
              </Link>
              <Link to="/products" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                All Products
              </Link>
              <Link to="/about" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                About Us
              </Link>
              <Link to="/contact" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Contact
              </Link>
              <Link to="/combos" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Best Combos
              </Link>
            </nav>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-4">Categories</h4>
            <nav className="flex flex-col gap-2">
              <Link to="/products?category=bracelets" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Bracelets
              </Link>
              <Link to="/products?category=rudraksha" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Rudraksha
              </Link>
              <Link to="/products?category=yantras" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Yantras
              </Link>
              <Link to="/products?category=pyramids" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Pyramids
              </Link>
              <Link to="/products?category=gemstones" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Gemstones
              </Link>
              <Link to="/products?category=murtis" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Murtis
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-4">Contact Us</h4>
            <div className="flex flex-col gap-4">
              <a
                href="tel:+919501147546"
                className="flex items-center gap-3 text-muted-foreground hover:text-gold transition-colors text-sm"
              >
                <Phone className="w-4 h-4 text-gold" />
                +91 95011 47546
              </a>
              <a
                href="mailto:support@astrovasturemedies.in"
                className="flex items-center gap-3 text-muted-foreground hover:text-gold transition-colors text-sm"
              >
                <Mail className="w-4 h-4 text-gold" />
                support@astrovasturemedies.in
              </a>
              <div className="flex items-start gap-3 text-muted-foreground text-sm">
                <MapPin className="w-4 h-4 text-gold mt-0.5" />
                <span>
                  40 Ft Road, Aggarwal Colony,<br />
                  Bathinda, Punjab 151001
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-amber-100">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm">
              © 2025 Astro Vastu Remedies. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Terms & Conditions
              </Link>
              <Link to="/refund-policy" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Refund Policy
              </Link>
              <Link to="/shipping-policy" className="text-muted-foreground hover:text-gold transition-colors text-sm">
                Shipping Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="bg-white/50 py-4 border-t border-amber-100">
        <div className="container mx-auto px-4 flex flex-wrap justify-center gap-6 lg:gap-12 text-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="text-gold">✓</span> 100% Authentic
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="text-gold">✓</span> Energized by Astrologers
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="text-gold">✓</span> 7 Day Returns
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="text-gold">✓</span> Secure Payments
          </div>
        </div>
      </div>
    </footer>
  );
};
